import React, { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { ComposePanel } from './compose-panel.component';
import { DraftSyncEffects } from './compose-panel.draft-sync.component';
import { useComposeDrafts } from './compose-panel.use-drafts';
import { useFreshTokenReport } from './compose-panel.use-token-report';
import { useVariableInsertion } from './compose-panel.use-insertion';
import { useComposeSend } from './compose-panel.use-send';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { AudienceSelector } from '../AudienceSelector/audience-selector.component';
import { resolveCommunicationContext } from '../AudienceSelector/audience-selector.helpers';
import { PreviewPanel } from '../PreviewPanel/preview-panel.component';
import { usePreviewRecipient } from '../PreviewPanel/preview-panel.use-recipient';
import { resolvePreviewRecipient } from '../PreviewPanel/preview-panel.helpers';
import { usePageContextSuggestions } from '../ContextSelector/context-selector.use-page-suggestions';
import { DraftsList } from './drafts-list.component';
import { IComposePanelFields, ITestSourceContext } from './compose-panel.types';
import { COMPOSE_FORM_ID } from './compose-panel.constants';
import { draftsStorageKey } from './compose-panel.drafts';
import { testRecipientsStorageKey } from './compose-panel.test-recipients';
import { validationSchema } from './compose-panel.validation';
import {
  buildSendAdHocCommunication,
  useCountAdHocRecipients
} from '../../../lib/services/communication.service';
import {
  resetComposeAudience,
  useDebouncedComposeAudience,
  useIsCommunicationDraftsView,
  useIsCommunicationPanelOpen,
  usePageCommunicationContext
} from '../../../lib/stores/communication.store';
import { SportTerminology } from '../../../lib/types';
import { useCommunicationPanelContext } from '../CommunicationPanel/communication-panel-context';
import { getCurrUser } from '../../../lib/services/user.service';
import { getActiveCompanyId } from '../../../lib/services/company.service';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';

export const ComposePanelContainer: React.FC = () => {
  const { panelAssessmentId, panelAssessmentName } = useCommunicationPanelContext();
  const { user } = getCurrUser();
  const companyId = getActiveCompanyId();
  const termLookup = useCompanySportTerminology();
  const sessionTerm = termLookup[SportTerminology.SESSION];
  const storageKey = draftsStorageKey(companyId, user && user._id);
  const testRecipientsKey = testRecipientsStorageKey(companyId, user && user._id);
  const isDraftsView = useIsCommunicationDraftsView();
  const isPanelOpen = useIsCommunicationPanelOpen();
  const pageRefs = usePageCommunicationContext();
  const { suggestions: pageSuggestions, loading: suggestionsLoading } =
    usePageContextSuggestions(panelAssessmentId, !isPanelOpen);

  const { sendAdHocCommunication } = buildSendAdHocCommunication();

  const [showPreview, setShowPreview] = useState(false);
  const {
    selected: selectedPreviewRecipient,
    fellOut: previewRecipientFellOut,
    selectRecipient: selectPreviewRecipient,
    resetToDefault: resetPreviewRecipient,
    clearToDefault: clearPreviewRecipient,
    dismissFellOut: dismissPreviewFellOut
  } = usePreviewRecipient();
  const drafts = useComposeDrafts({
    storageKey,
    isDraftsView,
    isPanelOpen,
    pageRefs,
    pageSuggestions,
    suggestionsLoading
  });

  // Reseed the compose-audience store on every (re)loaded document — cancel any
  // pending push from the prior draft and drop its "preview as" pick.
  useEffect(() => {
    resetComposeAudience({
      contexts: drafts.initialValues.contexts,
      audience: drafts.initialValues.audience,
      deliveryOption: drafts.initialValues.deliveryOption,
      content: {
        subject: drafts.initialValues.subject,
        bodyHtml: drafts.initialValues.bodyHtml,
        smsBody: drafts.initialValues.smsBody
      }
    });
    selectPreviewRecipient(null);
  }, [drafts.initialValues, selectPreviewRecipient]);

  // While the panel is closed, pass undefined assessmentId so the keepMounted
  // container skips the count query on every navigation (the hook's skip path).
  const {
    selection: debouncedSelection,
    isStale: countStale,
    isPreviewStale
  } = useDebouncedComposeAudience();
  // The count targets the same most-specific context the send will carry, so
  // the footer's numbers can never diverge from the mutation's audience.
  const debouncedContext = useMemo(
    () => resolveCommunicationContext(debouncedSelection.contexts, panelAssessmentId),
    [debouncedSelection.contexts, panelAssessmentId]
  );
  const { recipients, messages, unreachable, loading: countLoading } = useCountAdHocRecipients({
    assessmentId: isPanelOpen ? panelAssessmentId : undefined,
    criteria: debouncedSelection.audience,
    deliveryOption: debouncedSelection.deliveryOption,
    contextType: debouncedContext.contextType,
    contextId: debouncedContext.contextId
  });
  const recipientCountLoading = countLoading || countStale;

  // The debounced preview query, held latest report, and submit-time fresh-wait
  // live in this hook; the "preview as" recipient feeds the same call.
  const { latestReport, waitForFreshReport, preview, previewLoading } = useFreshTokenReport({
    debouncedSelection,
    isPreviewStale,
    panelAssessmentId,
    debouncedContext,
    isPanelOpen,
    playerAssessmentId: selectedPreviewRecipient ? selectedPreviewRecipient.key : undefined,
    onRecipientFellOut: resetPreviewRecipient,
    onRecipientCleared: clearPreviewRecipient
  });

  // Variable-insertion targeting (editor ref, last-focused field + caret refs,
  // insert-at-target handler) lives in its own colocated hook.
  const insertion = useVariableInsertion({ initialValues: drafts.initialValues });

  // Send/test-send system — submit + test handlers, the token-report gate, and
  // the success/error state, wired to the report gate and the drafts lifecycle.
  const send = useComposeSend({
    panelAssessmentId,
    sessionTerm,
    sendAdHocCommunication,
    waitForFreshReport,
    retireDraftAfterSend: drafts.retireDraftAfterSend,
    resetContent: drafts.resetContent
  });

  // Collapse the preview and drop any "preview as" pick when the panel scopes to a
  // different assessment; the send hook clears success/error state on the same key.
  useEffect(() => {
    setShowPreview(false);
    selectPreviewRecipient(null);
  }, [panelAssessmentId, selectPreviewRecipient]);

  if (!panelAssessmentId) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="textSecondary">
          No assessment available to compose a message.
        </Typography>
      </Box>
    );
  }

  if (isDraftsView) {
    return (
      <DraftsList
        drafts={drafts.drafts}
        onOpen={drafts.handleOpenDraft}
        onDelete={drafts.handleDeleteDraft}
        onNewMessage={drafts.handleNewMessage}
      />
    );
  }

  // Keyed per panel-open/draft; pre-filled rows open locked via the lag-free
  // initialContexts seed — see CLAUDE.md "Compose context propagation".
  const audienceSlot = (
    <AudienceSelector
      key={`${drafts.currentDraftId}:${isPanelOpen}`}
      assessmentId={panelAssessmentId}
      assessmentName={panelAssessmentName}
      skipOptionQueries={!isPanelOpen}
      initialContexts={drafts.initialValues.contexts}
      recipientCount={recipients}
      recipientCountLoading={recipientCountLoading}
    />
  );

  // All preview surfaces read the ONE debounced preview call's result; the picker
  // scopes to the same debounced audience/context the preview + send resolve.
  const previewSlot = (
    <PreviewPanel
      assessmentId={panelAssessmentId}
      criteria={debouncedSelection.audience}
      contextType={debouncedContext.contextType}
      contextId={debouncedContext.contextId}
      deliveryOption={debouncedSelection.deliveryOption}
      preview={preview}
      previewLoading={previewLoading}
      selectedRecipient={selectedPreviewRecipient}
      onSelectRecipient={selectPreviewRecipient}
      recipientFellOut={previewRecipientFellOut}
      onDismissFellOut={dismissPreviewFellOut}
      skipRecipientQuery={!isPanelOpen}
    />
  );

  // The test-send source picker reuses the preview-as machinery, gated to sends
  // whose content uses receiver-scoped tokens; defaults to the preview recipient.
  const testSource: ITestSourceContext = {
    assessmentId: panelAssessmentId,
    criteria: debouncedSelection.audience,
    contextType: debouncedContext.contextType,
    contextId: debouncedContext.contextId,
    usesReceiverScopedTokens: !!(latestReport && latestReport.usesReceiverScopedTokens),
    defaultSource: resolvePreviewRecipient(selectedPreviewRecipient, preview)
  };

  return (
    <Formik<IComposePanelFields>
      enableReinitialize
      validateOnMount
      initialValues={drafts.initialValues}
      validationSchema={validationSchema}
      onSubmit={send.handleSubmit}
    >
      {(form) => (
        <>
          <DraftSyncEffects persistDraft={drafts.persistDraft} />
          <FocusFirstInvalidField formId={COMPOSE_FORM_ID} />
          <ComposePanel
            form={form}
            assessmentId={panelAssessmentId}
            quillRef={insertion.quillRef}
            onFieldFocus={insertion.onFieldFocus}
            onQuillSelectionCapture={insertion.onQuillSelectionCapture}
            onInsertVariable={(token) => insertion.insertToken(token, form)}
            tokenReport={latestReport}
            sessionTerm={sessionTerm}
            recipientCount={recipients}
            messageCount={messages}
            unreachableCount={unreachable}
            recipientCountLoading={recipientCountLoading}
            showPreview={showPreview}
            onTogglePreview={() => setShowPreview((s) => !s)}
            showDraftRestoredBanner={drafts.showDraftRestoredBanner}
            loadingPageContext={drafts.loadingPageContext}
            onDismissDraftBanner={drafts.dismissDraftBanner}
            onDiscardDraft={drafts.handleDiscardDraft}
            submitError={send.submitError}
            queuedCount={send.queuedCount}
            // Compose-another also collapses the preview (container-owned view state).
            onComposeAnother={() => { send.handleComposeAnother(); setShowPreview(false); }}
            audienceSlot={audienceSlot}
            previewSlot={previewSlot}
            testSource={testSource}
            onSendTest={(emails, phones, testSourceId) =>
              send.handleSendTest(form.values, emails, phones, testSourceId)}
            testRecipientsStorageKey={testRecipientsKey}
          />
        </>
      )}
    </Formik>
  );
};
